import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { listReports, parseStructured, runReport, type ReportRow } from "../api/reports";
import { ReportListCard } from "../components/ReportListCard";
import StructuredReportDetail from "./StructuredReportDetail";
import MarkdownReportDetail from "./MarkdownReportDetail";

export default function Reports() {
  const { reportId } = useParams();
  const nav = useNavigate();
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    const r = await listReports();
    setRows(r);
    setLoading(false);
    if (!reportId && r[0]) nav(`/reports/${r[0].id}`, { replace: true });
  }, [reportId, nav]);

  useEffect(() => {
    void refresh();
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [refresh]);

  const selected = useMemo(
    () => rows.find((r) => r.id === reportId) ?? null,
    [rows, reportId],
  );

  const handleRegenerate = useCallback(async () => {
    if (!selected) return;
    setPolling(true);
    await runReport(selected.channelConfigId, selected.reportDate);
    const oldCreatedAt = new Date(selected.createdAt).getTime();
    const started = Date.now();
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      const r = await listReports({
        channelConfigId: selected.channelConfigId,
        reportDate: selected.reportDate,
      });
      const fresh = r.find(
        (x) => new Date(x.createdAt).getTime() > oldCreatedAt,
      );
      if (fresh || Date.now() - started > 60_000) {
        if (pollRef.current) window.clearInterval(pollRef.current);
        pollRef.current = null;
        setPolling(false);
        void refresh();
      }
    }, 3_000);
  }, [selected, refresh]);

  return (
    <div className="grid gap-[18px]" style={{ gridTemplateColumns: "340px 1fr" }}>
      <div className="min-w-0 flex flex-col gap-3">
        <div className="text-[10px] font-semibold text-text-muted uppercase tracking-widest">
          Reports · {rows.length}
        </div>
        {loading ? (
          <div className="text-[13px] text-text-muted">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-[13px] text-text-muted">
            No reports yet. Run <code>/daily-report</code> in Slack, or use
            Settings to add a channel config.
          </div>
        ) : (
          rows.map((r) => (
            <ReportListCard
              key={r.id}
              row={r}
              selected={r.id === reportId}
              onSelect={(id) => nav(`/reports/${id}`)}
            />
          ))
        )}
      </div>

      <div className="min-w-0">
        {selected ? (
          (() => {
            const payload = parseStructured(selected);
            return payload ? (
              <StructuredReportDetail
                row={selected}
                payload={payload}
                busy={polling}
                onRegenerate={handleRegenerate}
              />
            ) : (
              <MarkdownReportDetail
                row={selected}
                busy={polling}
                onRegenerate={handleRegenerate}
              />
            );
          })()
        ) : (
          !loading && (
            <div className="text-[13px] text-text-muted">Select a report.</div>
          )
        )}
      </div>
    </div>
  );
}
